import { getFunctions, httpsCallable } from "firebase/functions";
import { fyMonthKeys } from "./utils";

const functions = getFunctions(undefined, "europe-west1");
const getSnapshotFn  = httpsCallable(functions, "getSnapshot",  { timeout: 70000 });
const forceRefreshFn = httpsCallable(functions, "forceRefresh", { timeout: 540000 });
const setDriftConfigFn = httpsCallable(functions, "setDriftConfig", { timeout: 30000 });
const getPersonalDataFn = httpsCallable(functions, "getPersonalData", { timeout: 120000 });
const getLanthandelProdukterFn = httpsCallable(functions, "getLanthandelProdukter", { timeout: 60000 });
// Strömmat Claude-anrop med högt tokentak: ett helt år kan ta ett par minuter,
// så klientens timeout måste följa funktionens 300 s.
const budgetAssistFn = httpsCallable(functions, "budgetAssist", { timeout: 300000 });
// Oxceed-sandboxen: ett bolag tar ~30 s, tre skulle spränga klientens timeout.
const oxceedPreviewFn = httpsCallable(functions, "oxceedPreview", { timeout: 300000 });

// Oxceed-sandbox (admin): hämtar bokföringen ur Fortnox via Oxceed och
// returnerar exakt de dokument som skulle sparats — utan att spara dem.
export async function oxceedPreview(params) {
  const res = await oxceedPreviewFn(params);
  return res.data;
}

// Budgetassistenten (admin): schablon/resonera/chat-förslag för budgetrader.
// Skriver ALDRIG i budgeten — returnerar förslag som frontend diff:ar.
export async function budgetAssist(params) {
  const res = await budgetAssistFn(params);
  return res.data;
}

// Veckobrev (ledning): AI-utkast från veckans siffror. Returnerar { utkast }.
const veckobrevFn = httpsCallable(functions, "veckobrev", { timeout: 120000 });
export async function veckobrev(params) {
  const res = await veckobrevFn(params);
  return res.data;
}

// Styrelserapportens kommentarer (admin): AI-utkast från budget + fjolårsutfall.
const styrelseUtkastFn = httpsCallable(functions, "styrelseUtkast", { timeout: 120000 });
export async function styrelseUtkast(params) {
  const res = await styrelseUtkastFn(params);
  return res.data;
}

// Förklara-knappen (alla inloggade): AI-tolkning av ett korts innehåll.
const forklaraFn = httpsCallable(functions, "forklara", { timeout: 60000 });
export async function forklara(params) {
  const res = await forklaraFn(params);
  return res.data;
}

// Sportcenters produktförsäljning per dag × kategori — hela historiken.
// Ligger utanför snapshoten (12k rader) och hämtas bara av den som öppnar
// Produkter-fliken. Cachas i modulen: perioden byts i klienten, inte i nätet.
const sportcenterProdukterFn = httpsCallable(functions, 'getSportcenterProdukter', { timeout: 120000 });
let _scProd = null, _scProdPromise = null;
export async function getSportcenterProdukter() {
  if (_scProd) return _scProd;
  if (_scProdPromise) return _scProdPromise;
  _scProdPromise = (async () => {
    try {
      const res = await sportcenterProdukterFn();
      const d = res.data || {};
      _scProd = { kategorier: d.kategorier || [], rows: d.rows || [], updatedAt: d.updatedAt || null };
      return _scProd;
    } catch (e) {
      console.error('getSportcenterProdukter FAILED:', e.message);
      return { kategorier: [], rows: [], updatedAt: null, fel: e.message };
    } finally {
      _scProdPromise = null;
    }
  })();
  return _scProdPromise;
}

// Personal per månad × avdelning × anställningsform (budgetens fast/säsong).
const personalSplitFn = httpsCallable(functions, 'getPersonalSplit', { timeout: 300000 });
export async function getPersonalSplit(params) {
  const res = await personalSplitFn(params);
  return res.data;
}

// Fråga Birger (admin/ledning): agent som själv läser portalens verktyg.
const birgerFn = httpsCallable(functions, "birger", { timeout: 300000 });
export async function fragaBirger(params) {
  const res = await birgerFn(params);
  return res.data;
}

// Snapshot-cache: appen läser ALL data via ETT anrop till getSnapshot (som läser
// färdig-cachad data i Firestore). En schemalagd backend-funktion uppdaterar
// cachen var 15:e minut — inga live Qlik-anslutningar krävs för användaren.
let _snap = null, _snapTs = 0, _snapPromise = null;

async function getSnapshot(force = false) {
  const now = Date.now();
  if (!force && _snap && now - _snapTs < 5 * 60 * 1000) return _snap;
  if (!force && _snapPromise) return _snapPromise; // dedup samtidiga anrop
  _snapPromise = (async () => {
    try {
      const result = await getSnapshotFn();
      _snap = result.data || {};
      _snapTs = Date.now();
      return _snap;
    } finally {
      _snapPromise = null;
    }
  })();
  return _snapPromise;
}

export function getSnapshotUpdatedAt() {
  return _snap?.updatedAt ? new Date(_snap.updatedAt) : null;
}

// Admin: tvinga live-hämtning från Qlik + uppdatera cachen, läs sedan om snapshot
export async function forceRefresh() {
  await forceRefreshFn();
  return getSnapshot(true);
}

// Läs om cachen från Firestore (utan live-hämtning) — för manuell uppdatering
export async function refreshSnapshot() {
  return getSnapshot(true);
}

// Innevarande räkenskapsår (jul–jun) — ALLTID dynamiskt beräknat.
// (Var tidigare en hårdkodad lista → hela portalens YTD satt fast på
// RÅ 25/26 när nya räkenskapsåret började 1 juli 2026.)
const FY_MONTHS = fyMonthKeys();

async function fetchMonthly() {
  const snap = await getSnapshot();
  const { columns = [], rows = [] } = snap.monthly || {};
  if (!columns.length || !rows.length) return { monthly: [], ytd: {} };
  const dimCol = columns[0];

  const mapped = rows
    .filter(row => row[dimCol] !== null && row[dimCol] !== undefined)
    .map(row => {
      const arManad      = row[dimCol];
      const hotellLogi   = row["Rev Hotell Logi"]       || 0;
      const hotellRest   = row["Rev Hotellrestaurangen"] || 0;
      const hotellOvrigt = row["Rev Hotell Ovrigt"]      || 0;
      const brasserie    = row["Rev Brasserie"]          || 0;
      const brasseriedag   = row["Rev Brasserie Dag"]    || 0;
      const brasseriekvall = row["Rev Brasserie Kvall"]  || 0;
      const vavstugan      = row["Rev Vavstugan"]         || 0;
      const knallen      = row["Rev Knallen"]            || 0;
      const sportcenter  = row["Rev Sportcenter"]        || 0;
      const stugservice  = row["Rev Stugservice"]        || 0;
      const hotellTot    = hotellLogi + hotellRest + hotellOvrigt;
      const koncern      = hotellTot + brasserie + knallen + sportcenter + stugservice;

      const bokadeRum  = row["BokadeRumsnatter"]  || 0;
      const mojligaRum = row["MojligaRumsnatter"] || 0;
      const gastnatter = row["BokadeGastnatter"]  || 0;
      const belaggning = mojligaRum > 0 ? bokadeRum / mojligaRum : 0;

      const tb1Hotell      = row["TB1 Hotell Logi"]       || 0;
      const tb1Rest        = row["TB1 Hotellrestaurangen"] || 0;
      const tb1Brass       = row["TB1 Brasserie"]          || 0;
      const tb1BrassDag    = row["TB1 Brasserie Dag"]      || 0;
      const tb1BrassKvall  = row["TB1 Brasserie Kvall"]    || 0;
      const tb1Vavstugan   = row["TB1 Vavstugan"]           || 0;
      const tb1Knallen     = row["TB1 Knallen"]            || 0;
      const tb1Sport       = row["TB1 Sportcenter"]        || 0;
      const tb1Stug        = row["TB1 Stugservice"]        || 0;
      const tb1HotellOvrigt= row["TB1 Hotell Ovrigt"]      || 0;

      const timHotell      = row["Tim Hotell"]          || 0;
      const timRest        = row["Tim Rest"]            || 0;
      const timBrass       = row["Tim Brasserie"]       || 0;
      const timBrassDag    = row["Tim Brasserie Dag"]   || 0;
      const timBrassKvall  = row["Tim Brasserie Kvall"] || 0;
      const kvittonBrass      = row["Kvitton Brass"]       || 0;
      const kvittonBrassKvall = row["Kvitton Brass Kvall"] || 0;
      const kvittonBrassDag   = row["Kvitton Brass Dag"]   || 0;
      const portionerBrass      = row["Portioner Brass"]       || 0;
      const portionerBrassKvall = row["Portioner Brass Kvall"] || 0;
      const portionerBrassDag   = row["Portioner Brass Dag"]   || 0;
      const bokningarHotell = row["Bokningar Hotell"] || 0;
      const timKnallen     = row["Tim Knallen"]         || 0;
      const timSport       = row["Tim Sport"]           || 0;
      const timStug        = row["Tim Stug"]            || 0;
      const timRestFrukost = row["Tim Rest Frukost"]    || 0;
      const timRestLunch   = row["Tim Rest Lunch"]      || 0;
      const revRestFrukost = row["Rev Rest Frukost"]    || 0;
      const revRestLunch   = row["Rev Rest Lunch"]      || 0;
      const resenarKnallen = row["Resenärer Knallen"]  || 0;
      const lonHotellLogi  = row["Lon Hotell Logi"]        || 0;
      const lonHotellRest  = row["Lon Hotellrestaurangen"] || 0;
      const lonHotellOvrigt= row["Lon Hotell Ovrigt"]      || 0;
      const lonBrasserie   = row["Lon Brasserie"]          || 0;
      const lonKnallen     = row["Lon Knallen"]            || 0;
      const lonSport       = row["Lon Sportcenter"]        || 0;
      const lonStug        = row["Lon Stugservice"]        || 0;
      const lonBrassDag    = row["Lon Brasserie Dag"]      || 0;
      const lonBrassKvall  = row["Lon Brasserie Kvall"]    || 0;

      return {
        arManad, hotellLogi, hotellRest, hotellOvrigt, hotellTot,
        brasserie, brasseriedag, brasseriekvall, vavstugan, knallen, sportcenter, stugservice, koncern,
        gastnatter, belaggning, bokadeRum, mojligaRum, bokningarHotell,
        tb1Hotell, tb1Rest, tb1Brass, tb1BrassDag, tb1BrassKvall, tb1Vavstugan, tb1Knallen, tb1Sport, tb1Stug, tb1HotellOvrigt,
        timHotell, timRest, timBrass, timBrassDag, timBrassKvall, timKnallen, timSport, timStug,
        timRestFrukost, timRestLunch, revRestFrukost, revRestLunch, resenarKnallen,
        kvittonBrass, kvittonBrassKvall, kvittonBrassDag,
        portionerBrass, portionerBrassKvall, portionerBrassDag,
        lonHotellLogi, lonHotellRest, lonHotellOvrigt, lonBrasserie,
        lonKnallen, lonSport, lonStug, lonBrassDag, lonBrassKvall,
        _arr: [arManad, hotellTot, brasserie, knallen, sportcenter, stugservice, koncern, gastnatter, belaggning,
               tb1Hotell + tb1Rest,   // [9]  hotellTB1 (logi+rest, bakåtkompatibel)
               hotellLogi,            // [10]
               hotellRest,            // [11]
               hotellOvrigt,          // [12]
               tb1Hotell,             // [13]
               tb1Rest,               // [14]
               tb1HotellOvrigt,       // [15]
               timHotell,             // [16]
               timRest,               // [17]
               timBrass,              // [18]
               timKnallen,            // [19]
               timSport,              // [20]
               timStug,               // [21]
               timRestFrukost,        // [22]
               timRestLunch,          // [23]
               revRestFrukost,        // [24]
               revRestLunch,          // [25]
               brasseriedag,          // [26]
               brasseriekvall,        // [27]
               tb1Brass,              // [28]
               tb1BrassDag,           // [29]
               tb1BrassKvall,         // [30]
               timBrassDag,           // [31]
               timBrassKvall,         // [32]
               bokningarHotell,       // [33]
               bokadeRum,             // [34]
               mojligaRum,            // [35]
               vavstugan,             // [36]
               tb1Vavstugan,          // [37]
               tb1Knallen,            // [38]
               resenarKnallen,        // [39]
               tb1Sport,              // [40]
               tb1Stug,               // [41]
               lonHotellLogi,         // [42]
               lonHotellRest,         // [43]
               lonHotellOvrigt,       // [44]
               lonBrasserie,          // [45]
               lonKnallen,            // [46]
               lonSport,              // [47]
               lonStug,               // [48]
               lonBrassDag,           // [49]
               lonBrassKvall,         // [50]
        ],
      };
    });

  const withData = mapped.filter(r => r.koncern > 0).sort((a, b) => String(b.arManad).localeCompare(String(a.arManad)));
  const noData   = mapped.filter(r => r.koncern === 0).sort((a, b) => String(b.arManad).localeCompare(String(a.arManad)));
  const sorted   = [...withData, ...noData];

  const fyRows = sorted.filter(r => FY_MONTHS.includes(String(r.arManad)));
  const ytd = {
    sportcenter:        fyRows.reduce((s,r) => s + r.sportcenter, 0),
    hotellrestaurangen: fyRows.reduce((s,r) => s + r.hotellRest,  0),
    hotellLogi:         fyRows.reduce((s,r) => s + r.hotellLogi,  0),
    brasserieKerstin:   fyRows.reduce((s,r) => s + r.brasserie,   0),
    knallen:            fyRows.reduce((s,r) => s + r.knallen,     0),
    stugservice:        fyRows.reduce((s,r) => s + r.stugservice, 0),
    hotellOvrigt:       fyRows.reduce((s,r) => s + r.hotellOvrigt,0),
    koncern:            fyRows.reduce((s,r) => s + r.koncern,     0),
    tb1HotellLogi:      fyRows.reduce((s,r) => s + r.tb1Hotell,       0),
    tb1Hotellrest:      fyRows.reduce((s,r) => s + r.tb1Rest,         0),
    tb1HotellOvrigt:    fyRows.reduce((s,r) => s + r.tb1HotellOvrigt, 0),
    tb1Brasserie:       fyRows.reduce((s,r) => s + r.tb1Brass,        0),
    brasseriedag:       fyRows.reduce((s,r) => s + r.brasseriedag,    0),
    brasseriekvall:     fyRows.reduce((s,r) => s + r.brasseriekvall,  0),
    vavstugan:          fyRows.reduce((s,r) => s + r.vavstugan,       0),
    tb1Vavstugan:       fyRows.reduce((s,r) => s + r.tb1Vavstugan,    0),
    tb1BrassDag:        fyRows.reduce((s,r) => s + r.tb1BrassDag,     0),
    tb1BrassKvall:      fyRows.reduce((s,r) => s + r.tb1BrassKvall,   0),
    timBrassDag:        fyRows.reduce((s,r) => s + r.timBrassDag,     0),
    timBrassKvall:      fyRows.reduce((s,r) => s + r.timBrassKvall,   0),
    kvittonBrass:       fyRows.reduce((s,r) => s + r.kvittonBrass,    0),
    kvittonBrassKvall:  fyRows.reduce((s,r) => s + r.kvittonBrassKvall, 0),
    kvittonBrassDag:    fyRows.reduce((s,r) => s + r.kvittonBrassDag, 0),
    portionerBrass:     fyRows.reduce((s,r) => s + r.portionerBrass,  0),
    portionerBrassKvall:fyRows.reduce((s,r) => s + r.portionerBrassKvall, 0),
    portionerBrassDag:  fyRows.reduce((s,r) => s + r.portionerBrassDag,  0),
    bokningarHotell:    fyRows.reduce((s,r) => s + r.bokningarHotell, 0),
    tb1Knallen:         fyRows.reduce((s,r) => s + r.tb1Knallen,  0),
    resenarKnallen:     fyRows.reduce((s,r) => s + r.resenarKnallen, 0),
    tb1Sportcenter:     fyRows.reduce((s,r) => s + r.tb1Sport,    0),
    tb1Stugservice:     fyRows.reduce((s,r) => s + r.tb1Stug,     0),
    lonHotellLogi:      fyRows.reduce((s,r) => s + r.lonHotellLogi,   0),
    lonHotellrest:      fyRows.reduce((s,r) => s + r.lonHotellRest,   0),
    lonHotellOvrigt:    fyRows.reduce((s,r) => s + r.lonHotellOvrigt, 0),
    lonBrasserie:       fyRows.reduce((s,r) => s + r.lonBrasserie,    0),
    lonKnallen:         fyRows.reduce((s,r) => s + r.lonKnallen,      0),
    lonSportcenter:     fyRows.reduce((s,r) => s + r.lonSport,        0),
    lonStugservice:     fyRows.reduce((s,r) => s + r.lonStug,         0),
    gastnatter:         fyRows.reduce((s,r) => s + r.gastnatter,  0),
    bokadeRumsnatter:   fyRows.reduce((s,r) => s + r.bokadeRum,   0),
    mojligaRumsnatter:  fyRows.reduce((s,r) => s + r.mojligaRum,  0),
    timHotell:          fyRows.reduce((s,r) => s + r.timHotell,      0),
    timRest:            fyRows.reduce((s,r) => s + r.timRest,        0),
    timBrass:           fyRows.reduce((s,r) => s + r.timBrass,       0),
    timKnallen:         fyRows.reduce((s,r) => s + r.timKnallen,     0),
    timSport:           fyRows.reduce((s,r) => s + r.timSport,       0),
    timStug:            fyRows.reduce((s,r) => s + r.timStug,        0),
    timRestFrukost:     fyRows.reduce((s,r) => s + r.timRestFrukost, 0),
    timRestLunch:       fyRows.reduce((s,r) => s + r.timRestLunch,   0),
    revRestFrukost:     fyRows.reduce((s,r) => s + r.revRestFrukost, 0),
    revRestLunch:       fyRows.reduce((s,r) => s + r.revRestLunch,   0),
  };

  return { monthly: sorted.map(r => r._arr), ytd };
}

async function fetchDaily() {
  const snap = await getSnapshot();
  const { columns = [], rows = [] } = snap.daily || {};
  if (!columns.length || !rows.length) return [];
  const dimCol = columns[0];

  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 90);
  const cutoffStr = cutoff.toISOString().slice(0, 10);

  const daily = rows
    .filter(row => row[dimCol] && row[dimCol] >= cutoffStr)
    .map(row => ({
      datum:          row[dimCol],
      hotellLogi:     row["Rev Hotell Logi"]        || 0,
      hotellRest:     row["Rev Hotellrestaurangen"]  || 0,
      hotellOvrigt:   row["Rev Hotell Ovrigt"]       || 0,
      brasserie:      row["Rev Brasserie"]            || 0,
      brasseriedag:   row["Rev Brasserie Dag"]        || 0,
      brasseriekvall: row["Rev Brasserie Kvall"]      || 0,
      lonBrassDag:    row["Lon Brasserie Dag"]        || 0,
      lonBrassKvall:  row["Lon Brasserie Kvall"]      || 0,
      vavstugan:      row["Rev Vavstugan"]             || 0,
      timBrass:       row["Tim Brasserie"]            || 0,
      timBrassDag:    row["Tim Brasserie Dag"]        || 0,
      timBrassKvall:  row["Tim Brasserie Kvall"]      || 0,
      tb1Brass:       row["TB1 Brasserie"]            || 0,
      knallen:        row["Rev Knallen"]              || 0,
      sportcenter:    row["Rev Sportcenter"]          || 0,
      stugservice:    row["Rev Stugservice"]          || 0,
      tb1Logi:        row["TB1 Hotell Logi"]          || 0,
      tb1Rest:        row["TB1 Hotellrestaurangen"]   || 0,
      tb1Ovrigt:      row["TB1 Hotell Ovrigt"]        || 0,
      lonHotellLogi:  row["Lon Hotell Logi"]          || 0,
      lonHotellRest:  row["Lon Hotellrestaurangen"]   || 0,
      timHotell:      row["Tim Hotell"]               || 0,
      timRest:        row["Tim Rest"]                 || 0,
      timRestFrukost: row["Tim Rest Frukost"]         || 0,
      timRestLunch:   row["Tim Rest Lunch"]           || 0,
      revRestFrukost: row["Rev Rest Frukost"]         || 0,
      revRestLunch:   row["Rev Rest Lunch"]           || 0,
      totalt: (row["Rev Hotell Logi"] || 0) + (row["Rev Hotellrestaurangen"] || 0) +
              (row["Rev Hotell Ovrigt"] || 0) + (row["Rev Brasserie"] || 0) +
              (row["Rev Knallen"] || 0) + (row["Rev Sportcenter"] || 0),
    }))
    .sort((a, b) => b.datum.localeCompare(a.datum));

  return daily;
}

async function fetchBookings() {
  try {
    const snap = await getSnapshot();
    const d = snap.bookings || {};
    console.log('Bookings OK:', d.totalCheckins, 'checkins,', d.totalCheckouts, 'checkouts');
    return {
      todayStr:           d.todayStr           || null,
      belaggningDays:     d.belaggningDays     || [],
      belaggningLastYear: d.belaggningLastYear || [],
      planDays:           d.planDays           || [],
      planDaysLastYear:   d.planDaysLastYear   || [],
      checkins:           d.checkins           || [],
      checkouts:       d.checkouts       || [],
      totalCheckins:   d.totalCheckins   || 0,
      totalCheckouts:  d.totalCheckouts  || 0,
      totalGasterIdag: d.totalGasterIdag || 0,
    };
  } catch (e) {
    console.error('fetchBookings error:', e.message);
    return {
      todayStr: null, belaggningDays: [], belaggningLastYear: [], planDays: [], planDaysLastYear: [], checkins: [], checkouts: [],
      totalCheckins: 0, totalCheckouts: 0, totalGasterIdag: 0,
    };
  }
}

async function fetchNewBookings() {
  try {
    const snap = await getSnapshot();
    const d = snap.newbookings || {};
    console.log(`Nya bokningar: idag ${d.idag?.antal}, igår ${d.igar?.antal}`);
    return {
      idag: d.idag || { antal: 0, varde: 0, bokningar: [] },
      igar: d.igar || { antal: 0, varde: 0, bokningar: [] },
    };
  } catch (e) {
    console.error('fetchNewBookings error:', e.message);
    return {
      idag: { antal: 0, varde: 0, bokningar: [] },
      igar: { antal: 0, varde: 0, bokningar: [] },
    };
  }
}

async function fetchWeekly() {
  const snap = await getSnapshot();
  const { columns = [], rows = [] } = snap.weekly || {};
  if (!columns.length || !rows.length) return [];
  const dimCol = columns[0];
  console.log(`fetchWeekly: ${rows.length} rows, dimCol="${dimCol}", sample:`, JSON.stringify(rows.slice(0,3).map(r => r[dimCol])));

  const mapped = rows
    .filter(row => row[dimCol] !== null && row[dimCol] !== undefined)
    .map(row => {
      // Strip any locale-specific formatting (spaces, commas) before parsing
      const raw     = row[dimCol];
      const yrWeek  = typeof raw === 'number' ? Math.round(raw) : parseInt(String(raw).replace(/[^\d]/g, ''));
      const hotellLogi   = row["Rev Hotell Logi"]       || 0;
      const hotellRest   = row["Rev Hotellrestaurangen"] || 0;
      const hotellOvrigt = row["Rev Hotell Ovrigt"]      || 0;
      const hotellTot    = hotellLogi + hotellRest + hotellOvrigt;
      const brasserie    = row["Rev Brasserie"]          || 0;
      const knallen      = row["Rev Knallen"]            || 0;
      const sportcenter  = row["Rev Sportcenter"]        || 0;
      const stugservice  = row["Rev Stugservice"]        || 0;
      const bokadeRum    = row["BokadeRumsnatter"]  || 0;
      const mojligaRum   = row["MojligaRumsnatter"] || 0;
      const belaggning   = mojligaRum > 0 ? bokadeRum / mojligaRum : 0;
      const tb1L         = row["TB1 Hotell Logi"]        || 0;
      const tb1R         = row["TB1 Hotellrestaurangen"]  || 0;
      const tb1O         = row["TB1 Hotell Ovrigt"]       || 0;
      const tb1          = tb1L + tb1R + tb1O;
      const timHotell      = row["Tim Hotell"]       || 0;
      const timRest        = row["Tim Rest"]         || 0;
      const timBrass       = row["Tim Brasserie"]    || 0;
      const timKnallen     = row["Tim Knallen"]      || 0;
      const timSport       = row["Tim Sport"]        || 0;
      const timStug        = row["Tim Stug"]         || 0;
      const timRestFrukost = row["Tim Rest Frukost"] || 0;
      const timRestLunch   = row["Tim Rest Lunch"]   || 0;
      const revRestFrukost = row["Rev Rest Frukost"] || 0;
      const revRestLunch   = row["Rev Rest Lunch"]   || 0;
      const resenarKnallen = row["Resenärer Knallen"] || 0;
      const lonKnallen     = row["Lon Knallen"]       || 0;
      const tb1Knallen     = row["TB1 Knallen"]       || 0;
      return { yrWeek, hotellTot, belaggning, tb1,
               _arr: [yrWeek, hotellTot, belaggning, tb1,
                      hotellLogi, hotellRest, hotellOvrigt, tb1L, tb1R, tb1O,
                      timHotell, timRest, timBrass, timKnallen, timSport, timStug,
                      brasserie, knallen, sportcenter, stugservice,
                      timRestFrukost, timRestLunch, revRestFrukost, revRestLunch,
                      resenarKnallen, lonKnallen, tb1Knallen] };
      //              [4]        [5]       [6]         [7]  [8]  [9]
      //              [10]       [11]      [12]        [13] [14] [15]
      //              [16]       [17]      [18]        [19]
      //              [20]       [21]      [22]        [23]
      //              [24] resenarKnallen  [25] lonKnallen  [26] tb1Knallen
    });

  return mapped
    .filter(r => r.hotellTot > 0 || r.belaggning > 0 || (r._arr[17] || 0) > 0) // [17] = Knallen (veckor då bara Knallen har försäljning ska med)
    .sort((a, b) => b.yrWeek - a.yrWeek)
    .map(r => r._arr);
}

export async function getRoomStats() {
  const snap = await getSnapshot();
  return snap.roomstats?.rooms || [];
}

export async function getLedningData() {
  try {
    const snap = await getSnapshot();
    return snap.ledning?.rows || [];
  } catch (e) {
    console.error('getLedningData FAILED:', e.message);
    return [];
  }
}

// Finkornig dag×enhet-data för flexibelt periodfilter. Expanderar komprimerat
// format { enheter, rows:[[datum,enhetIdx,intakt,kostnad]] } → [{datum,enhet,intakt,kostnad}].
export async function getDailyUnits() {
  try {
    const snap = await getSnapshot();
    const du = snap.daily_units;
    if (!du || !Array.isArray(du.rows)) return [];
    const enheter = du.enheter || [];
    return du.rows.map(([datum, ei, intakt, kostnad, lon]) => ({
      datum, enhet: enheter[ei] || '?', intakt: intakt || 0, kostnad: kostnad || 0, lon: lon || 0,
    }));
  } catch (e) {
    console.error('getDailyUnits FAILED:', e.message);
    return [];
  }
}

// Vädret (Netatmo): { nu: {station, tid, ute, inne, vind, regn}, byDay: {datum: {min,max,regn,byarMax}} }
export async function getVader() {
  try {
    const snap = await getSnapshot();
    return snap.vader || null;
  } catch (e) {
    console.error('getVader:', e);
    return null;
  }
}

// Lanthandeln (HonestBox): { storeName, days:[[datum,oms,marginal,kvitton]], daysLY,
// summering, varugrupper:{vecka,manad}, betalsatt, lager:{antalArtiklar,varde,pafyllnad}, dorrOppen }
export async function getLanthandel() {
  try {
    const snap = await getSnapshot();
    return snap.lanthandel || null;
  } catch (e) {
    console.error('getLanthandel FAILED:', e.message);
    return null;
  }
}

// Lanthandeln: sålda produkter per period (LIVE mot HonestBox via callable).
// Cache med TTL: perioder som innehåller idag åldras på 5 min, historiska 30 min.
const _lhProdCache = new Map();
export async function getLanthandelProdukter(from, to) {
  const key = `${from}|${to}`;
  const today = new Date().toLocaleDateString('sv-SE');
  const ttl = to >= today ? 5 * 60000 : 30 * 60000;
  const hit = _lhProdCache.get(key);
  if (hit && Date.now() - hit.ts < ttl) return hit.data;
  const res = await getLanthandelProdukterFn({ from, to });
  _lhProdCache.set(key, { ts: Date.now(), data: res.data });
  if (_lhProdCache.size > 30) _lhProdCache.delete(_lhProdCache.keys().next().value);
  return res.data;
}

// Driftmodell: fasta kostnader (kr/mån) — { fastKostnPerManad, months:{YYYYMM:kr}, updatedAt, updatedBy }
export async function getDriftConfig() {
  try {
    const snap = await getSnapshot();
    return snap.drift_config || null;
  } catch (e) {
    console.error('getDriftConfig FAILED:', e.message);
    return null;
  }
}

// Admin: spara driftmodellens fasta kostnader, läs sedan om snapshot
export async function setDriftConfig(patch) {
  await setDriftConfigFn(patch);
  return getSnapshot(true);
}

// Personal/löner per person & dag (KÄNSLIGT — admin-spärrad live-fråga, cachas
// bara i minnet i denna flik). rows: [[datum, namn, enhet, transtyp, lon, tim]]
const _persCache = new Map();
export async function getPersonalData(from, to) {
  const key = from + '|' + to;
  if (_persCache.has(key)) return _persCache.get(key);
  const result = await getPersonalDataFn({ from, to });
  const data = result.data || { rows: [] };
  _persCache.set(key, data);
  if (_persCache.size > 20) _persCache.delete(_persCache.keys().next().value); // enkel LRU
  return data;
}

// Beläggning: daglig serie hotell + förmedling → { byDatum:{datum:{rumRum,rumG,formObj,formG}}, totalRooms, today }
export async function getBelaggning() {
  try {
    const snap = await getSnapshot();
    const b = snap.belaggning;
    if (!b || !Array.isArray(b.rows)) return { byDatum: {}, totalRooms: 45, today: null };
    const byDatum = {};
    for (const [datum, rumRum, rumG, formObj, formG, logi, rumRumO, rumGO, mat] of b.rows) {
      byDatum[datum] = {
        rumRum: rumRum || 0, rumG: rumG || 0, formObj: formObj || 0, formG: formG || 0, logi: logi || 0,
        mat: mat || 0,                                       // förbokad restaurang (gammal cache saknar fältet)
        rumRumO: rumRumO != null ? rumRumO : (rumRum || 0), // inkl. offert (fallback till bekräftat för gammal cache)
        rumGO: rumGO != null ? rumGO : (rumG || 0),
      };
    }
    return { byDatum, totalRooms: b.totalRooms || 45, today: b.today || null, paceLive: snap.pace_live || { weeks: {} } };
  } catch (e) {
    console.error('getBelaggning FAILED:', e.message);
    return { byDatum: {}, totalRooms: 45, today: null, paceLive: { weeks: {} } };
  }
}

// Brasserie dag/kväll per dag (full historik) → { datum: { oms:[dag,kvall], pers:[dag,kvall] } }
// pers (lön) returneras som absolutbelopp. Används av planeringsvyns dag/kväll-toggle.
export async function getBrasserieSplit() {
  try {
    const snap = await getSnapshot();
    const bs = snap.daily_brass_split;
    if (!bs || !Array.isArray(bs.rows)) return {};
    const out = {};
    for (const [datum, revDag, revKvall, lonDag, lonKvall, timDag, timKvall] of bs.rows) {
      out[datum] = {
        oms: [revDag || 0, revKvall || 0],
        pers: [Math.abs(lonDag || 0), Math.abs(lonKvall || 0)],
        tim: [timDag || 0, timKvall || 0], // saknas i gammal cache → 0 tills nästa hämtning
      };
    }
    return out;
  } catch (e) {
    console.error('getBrasserieSplit FAILED:', e.message);
    return {};
  }
}

// Arbetstimmar + stämplad lön per dag per enhet.
// → { order:[enhetsnamn], byDatum:{datum:[h...]}, lonByDatum:{datum:[kr...]}, harLon }
// Raden i cachen är [datum, ...timmar, ...lön] där lönedelen tillkom aug 2026.
// harLon=false på gammal cache → bemanningsvyn säger "väntar på nästa hämtning"
// i stället för att räkna med noll i fasta löner.
export async function getDailyHours() {
  const tomt = { order: [], byDatum: {}, lonByDatum: {}, harLon: false };
  try {
    const snap = await getSnapshot();
    const dh = snap.daily_hours;
    if (!dh || !Array.isArray(dh.rows)) return tomt;
    const order = dh.order || [];
    const n = order.length;
    const harLon = !!dh.harLon && dh.rows.every(r => r.length >= 1 + n * 2);
    const byDatum = {}, lonByDatum = {};
    for (const r of dh.rows) {
      byDatum[r[0]] = r.slice(1, 1 + n);
      if (harLon) lonByDatum[r[0]] = r.slice(1 + n, 1 + n * 2);
    }
    return { order, byDatum, lonByDatum, harLon };
  } catch (e) {
    console.error('getDailyHours FAILED:', e.message);
    return tomt;
  }
}

export async function getTopProducts() {
  const empty = { dag: [], kvall: [] };
  const emptyK = { today: [], yesterday: [], week: [], month: [], threemonths: [] };
  const fallback = { today: empty, yesterday: empty, week: empty, month: empty, threemonths: empty, knallen: emptyK, sportcenter: emptyK, stugservice: { kunder: emptyK, tjanster: emptyK } };
  try {
    const snap = await getSnapshot();
    const p = snap.topproducts?.products || {};
    // Djup-merge per nyckel: gammal cache utan 'today' ska inte krascha blocken
    const out = { ...fallback, ...p };
    out.knallen = { ...emptyK, ...(p.knallen || {}) };
    out.sportcenter = { ...emptyK, ...(p.sportcenter || {}) };
    out.stugservice = { kunder: { ...emptyK, ...(p.stugservice?.kunder || {}) }, tjanster: { ...emptyK, ...(p.stugservice?.tjanster || {}) } };
    return out;
  } catch (e) {
    console.error('getTopProducts FAILED:', e.message);
    return fallback;
  }
}

export async function getWeeklyData() {
  return fetchWeekly();
}

export async function getAllData() {
  // All data ligger i samma snapshot — getSnapshot dedupar/cachar, så de fyra
  // anropen nedan ger bara EN nätverksförfrågan.
  const monthlyData = await fetchMonthly();
  const dailyData = await fetchDaily();
  const bookingsData = await fetchBookings();
  const newBookingsData = await fetchNewBookings();

  const out = { ...monthlyData, daily: dailyData, bookings: bookingsData, newBookings: newBookingsData };
  console.log('getAllData complete. Monthly:', (monthlyData.monthly || []).length, '| Daily:', dailyData.length, '| Bookings checkins:', bookingsData.totalCheckins, '| Nya bokningar idag:', newBookingsData.idag?.antal);
  return out;
}
